import Phaser from 'phaser';
import { socketManager } from '../socket/SocketManager';

export class LoginScene extends Phaser.Scene {
  private usernameText = '';
  private passwordText = '';
  private activeField: 'username' | 'password' = 'username';
  private errorText!: Phaser.GameObjects.Text;
  private usernameDisplay!: Phaser.GameObjects.Text;
  private passwordDisplay!: Phaser.GameObjects.Text;
  private usernameBorder!: Phaser.GameObjects.Rectangle;
  private passwordBorder!: Phaser.GameObjects.Rectangle;

  constructor() {
    super({ key: 'LoginScene' });
  }

  create(): void {
    // Try auto-reconnect with saved token
    const savedToken = socketManager.getSavedToken();
    if (savedToken) {
      let resolved = false;
      socketManager.connect(savedToken, false);

      // On successful connection → go to game
      const checkInterval = setInterval(() => {
        if (resolved) return;
        if (socketManager.isConnected()) {
          resolved = true;
          clearInterval(checkInterval);
          this.scene.start('GameScene');
        }
      }, 200);

      // Timeout: if not connected after 3s, give up
      setTimeout(() => {
        if (!resolved) {
          resolved = true;
          clearInterval(checkInterval);
          socketManager.clearSavedToken();
          socketManager.disconnect();
        }
      }, 3000);
    }

    const { width, height } = this.scale;
    const cx = width / 2;
    const cy = height / 2;

    // Title
    this.add
      .text(cx, cy - 160, 'MAFIA CITY', {
        fontSize: '42px',
        fontFamily: 'monospace',
        color: '#ff6b35',
      })
      .setOrigin(0.5);

    // Username field
    this.add
      .text(cx - 120, cy - 70, 'Username:', {
        fontSize: '16px',
        fontFamily: 'monospace',
        color: '#aaaaaa',
      })
      .setOrigin(0, 0.5);

    this.usernameBorder = this.add
      .rectangle(cx, cy - 40, 260, 32, 0x333333)
      .setStrokeStyle(2, 0xff6b35);

    this.usernameDisplay = this.add
      .text(cx - 120, cy - 40, '', {
        fontSize: '16px',
        fontFamily: 'monospace',
        color: '#ffffff',
      })
      .setOrigin(0, 0.5);

    // Password field
    this.add
      .text(cx - 120, cy + 10, 'Password:', {
        fontSize: '16px',
        fontFamily: 'monospace',
        color: '#aaaaaa',
      })
      .setOrigin(0, 0.5);

    this.passwordBorder = this.add
      .rectangle(cx, cy + 40, 260, 32, 0x333333)
      .setStrokeStyle(2, 0x555555);

    this.passwordDisplay = this.add
      .text(cx - 120, cy + 40, '', {
        fontSize: '16px',
        fontFamily: 'monospace',
        color: '#ffffff',
      })
      .setOrigin(0, 0.5);

    // Buttons
    const loginBtn = this.add
      .text(cx - 70, cy + 90, '[ LOGIN ]', {
        fontSize: '18px',
        fontFamily: 'monospace',
        color: '#4caf50',
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    const registerBtn = this.add
      .text(cx + 70, cy + 90, '[ REGISTER ]', {
        fontSize: '18px',
        fontFamily: 'monospace',
        color: '#3a6bc4',
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    // Error text
    this.errorText = this.add
      .text(cx, cy + 130, '', {
        fontSize: '14px',
        fontFamily: 'monospace',
        color: '#ff4444',
      })
      .setOrigin(0.5);

    // Click to switch fields
    this.usernameBorder.setInteractive({ useHandCursor: true });
    this.passwordBorder.setInteractive({ useHandCursor: true });

    this.usernameBorder.on('pointerdown', () => {
      this.activeField = 'username';
      this.updateFieldStyles();
    });

    this.passwordBorder.on('pointerdown', () => {
      this.activeField = 'password';
      this.updateFieldStyles();
    });

    loginBtn.on('pointerdown', () => this.handleLogin());
    registerBtn.on('pointerdown', () => this.handleRegister());

    // Keyboard input
    this.input.keyboard!.on('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        event.preventDefault();
        this.activeField =
          this.activeField === 'username' ? 'password' : 'username';
        this.updateFieldStyles();
        return;
      }

      if (event.key === 'Enter') {
        this.handleLogin();
        return;
      }

      if (event.key === 'Backspace') {
        if (this.activeField === 'username') {
          this.usernameText = this.usernameText.slice(0, -1);
        } else {
          this.passwordText = this.passwordText.slice(0, -1);
        }
        this.updateDisplays();
        return;
      }

      if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
        if (this.activeField === 'username' && this.usernameText.length < 32) {
          this.usernameText += event.key;
        } else if (
          this.activeField === 'password' &&
          this.passwordText.length < 128
        ) {
          this.passwordText += event.key;
        }
        this.updateDisplays();
      }
    });
  }

  private updateFieldStyles(): void {
    this.usernameBorder.setStrokeStyle(
      2,
      this.activeField === 'username' ? 0xff6b35 : 0x555555,
    );
    this.passwordBorder.setStrokeStyle(
      2,
      this.activeField === 'password' ? 0xff6b35 : 0x555555,
    );
  }

  private updateDisplays(): void {
    this.usernameDisplay.setText(this.usernameText);
    this.passwordDisplay.setText('*'.repeat(this.passwordText.length));
  }

  private async handleLogin(): Promise<void> {
    if (!this.usernameText || !this.passwordText) {
      this.errorText.setText('Please fill in all fields');
      return;
    }

    try {
      this.errorText.setText('Connecting...');
      const token = await socketManager.login(
        this.usernameText,
        this.passwordText,
      );
      socketManager.connect(token);
      this.scene.start('GameScene');
    } catch {
      this.errorText.setText('Invalid credentials');
    }
  }

  private async handleRegister(): Promise<void> {
    if (!this.usernameText || !this.passwordText) {
      this.errorText.setText('Please fill in all fields');
      return;
    }

    if (this.usernameText.length < 3) {
      this.errorText.setText('Username must be at least 3 characters');
      return;
    }

    if (this.passwordText.length < 6) {
      this.errorText.setText('Password must be at least 6 characters');
      return;
    }

    try {
      this.errorText.setText('Creating account...');
      const token = await socketManager.register(
        this.usernameText,
        this.passwordText,
      );
      socketManager.connect(token);
      this.scene.start('GameScene');
    } catch (err) {
      this.errorText.setText(
        err instanceof Error ? err.message : 'Registration failed',
      );
    }
  }
}
